// primitives.tsx — PharmacyIQ web-admin component kit.
// Ported from the prototype's primitives.jsx; inline styles are unchanged.
import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import { Icon } from './icons';
import type { IconName } from './icons';
import { useStore } from '@/store/store';

export function Logo({ dark = false, size = 22 }: { dark?: boolean; size?: number }) {
  const src = dark ? '/images/logo-white.png' : '/images/logo-dark.png';
  return (
    <img
      src={src}
      alt="Pharmacy-IQ"
      style={{
        height: size,
        width: 'auto',
        objectFit: 'contain',
        display: 'inline-block',
      }}
    />
  );
}

const BTN_BASE: CSSProperties = {
  border: 'none',
  borderRadius: 'var(--radius-md)',
  fontWeight: 700,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'var(--space-2)',
  whiteSpace: 'nowrap',
  transition: 'all var(--duration-fast) var(--ease)',
};
// Control heights standardised to the WS-A tokens (sm 32 / md 40); the large
// size stays 48 (8-grid, login/hero only).
const BTN_SIZE: Record<string, CSSProperties> = {
  s: { height: 'var(--control-h-sm)', fontSize: 'var(--fs-body)', padding: '0 var(--space-3)' },
  m: { height: 'var(--control-h-md)', fontSize: 'var(--fs-emphasis)', padding: '0 var(--space-4)' },
  l: { height: 48, fontSize: 'var(--fs-emphasis)', padding: '0 var(--space-5)' },
};

export type ButtonVariant = 'primary' | 'soft' | 'outlined' | 'text' | 'ghosterr';
export type ButtonColor = 'primary' | 'error' | 'success';

export interface ButtonProps {
  children?: ReactNode;
  variant?: ButtonVariant;
  size?: 's' | 'm' | 'l';
  icon?: IconName;
  iconRight?: IconName;
  // The underlying <button> forwards its event, so handlers may take it.
  onClick?: (e?: ReactMouseEvent<HTMLButtonElement>) => void;
  color?: ButtonColor;
  disabled?: boolean;
  style?: CSSProperties;
  title?: string;
  /** Accessible name for icon-only buttons (WCAG button-name, BUG-A11Y-001). */
  ariaLabel?: string;
  /** Stable data-testid for the QA suites (Part B). */
  testId?: string;
}

export function Button({
  children,
  variant = 'primary',
  size = 'm',
  icon,
  iconRight,
  onClick,
  color = 'primary',
  disabled,
  style,
  title,
  ariaLabel,
  testId,
}: ButtonProps) {
  // Icon-only buttons render no text node, so screen readers have nothing to
  // announce. Give them an accessible name from ariaLabel (preferred) or the
  // tooltip title (BUG-A11Y-001 — axe `button-name`).
  const iconOnly =
    (!!icon || !!iconRight) &&
    (children === undefined || children === null || children === false || children === '');
  const computedAriaLabel = ariaLabel ?? (iconOnly ? title : undefined);
  const [hover, setHover] = useState(false);
  const c = (
    {
      primary: ['var(--primary-main)', 'var(--primary-dark)', 'var(--primary-contrast)'],
      error: ['var(--error-solid)', 'var(--error-solid)', 'var(--primary-contrast)'],
      // solid success fills use the theme-stable --success-solid: in dark, --success-dark
      // is a bright green that leaves white text at 1.74:1 (drawer-audit finding)
      success: ['var(--success-solid)', 'var(--success-solid)', 'var(--primary-contrast)'],
    } as Record<string, string[]>
  )[color] || ['var(--primary-main)', 'var(--primary-dark)', 'var(--primary-contrast)'];
  const variants: Record<string, CSSProperties> = {
    primary: {
      background: hover ? c[1] : c[0],
      color: c[2],
      boxShadow: disabled
        ? 'none'
        : color === 'primary'
          ? 'var(--shadow-button)'
          : color === 'error'
            ? 'var(--shadow-button-error)'
            : 'var(--shadow-button-success)',
    },
    soft: {
      background: hover ? 'var(--primary-soft)' : 'var(--primary-lighter)',
      color: 'var(--primary-dark)',
    },
    outlined: {
      background: hover ? 'var(--primary-lighter)' : 'var(--bg-card)',
      // -ink, not -main: AA as text on the dark card surface (WS-B a11y).
      color: 'var(--primary-ink)',
      boxShadow: 'inset 0 0 0 1px var(--primary-light)',
    },
    text: {
      background: hover ? 'var(--grey-12)' : 'transparent',
      color: 'var(--text-secondary)',
    },
    ghosterr: {
      background: hover ? 'var(--error-soft)' : 'transparent',
      color: 'var(--error-dark)',
      boxShadow: 'inset 0 0 0 1px var(--error-soft)',
    },
  };
  return (
    <button
      onClick={disabled ? undefined : onClick}
      title={title}
      aria-label={computedAriaLabel}
      data-testid={testId}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        ...BTN_BASE,
        ...BTN_SIZE[size],
        ...variants[variant],
        ...(disabled
          ? { opacity: 0.45, cursor: 'not-allowed', boxShadow: 'none', filter: 'grayscale(.3)' }
          : null),
        ...style,
      }}
    >
      {icon && <Icon name={icon} size={size === 's' ? 16 : 18} />}
      {children}
      {iconRight && <Icon name={iconRight} size={size === 's' ? 16 : 18} />}
    </button>
  );
}

// Permission-gated button: disabled + lock tooltip when the active role lacks the capability.
export function GatedButton({
  cap,
  children,
  lockMsg,
  ...rest
}: ButtonProps & { cap: string; lockMsg?: string }) {
  const { can } = useStore();
  if (can(cap)) return <Button {...rest}>{children}</Button>;
  return (
    <span style={{ display: 'inline-flex', position: 'relative' }} className="gb-wrap">
      <Button
        {...rest}
        disabled
        icon={rest.icon || 'lock'}
        title={lockMsg || 'Your role cannot perform this action'}
      >
        {children}
      </Button>
      <span className="gb-tip" style={tipStyle}>
        {lockMsg || 'Not permitted for your role'}
      </span>
    </span>
  );
}
const tipStyle: CSSProperties = {
  position: 'absolute',
  bottom: 'calc(100% + 8px)',
  left: '50%',
  transform: 'translateX(-50%)',
  background: 'var(--aubergine-900)',
  color: 'var(--primary-contrast)',
  fontSize: 'var(--fs-body-sm)',
  fontWeight: 600,
  padding: '6px 10px',
  borderRadius: 'var(--radius-md)',
  whiteSpace: 'nowrap',
  opacity: 0,
  pointerEvents: 'none',
  transition: 'opacity var(--duration-fast) var(--ease)',
  zIndex: 80,
};

const CHIP_MAP: Record<string, [string, string]> = {
  // every tinted tone uses the OPAQUE -lighter tier so chip text clears AA on
  // ANY surface incl. the dark drawer panel (axe drawer-open audit — the -soft
  // translucents blend under 4.5:1 there for error/info).
  success: ['var(--success-lighter)', 'var(--success-dark)'],
  warning: ['var(--warning-lighter)', 'var(--warning-dark)'],
  error: ['var(--error-lighter)', 'var(--error-dark)'],
  info: ['var(--info-lighter)', 'var(--info-dark)'],
  // default fg deepened from --text-secondary to --grey-700 for AA contrast on the grey-16 tint (BUG-A11Y-001)
  default: ['var(--grey-16)', 'var(--grey-700)'],
  violet: ['var(--primary-lighter)', 'var(--primary-dark)'],
  // solidError bg deepened to a theme-stable dark red so white text clears AA (was --error-main #ff5630 = 3.17:1) (BUG-A11Y-001)
  solidError: ['var(--error-solid)', 'var(--primary-contrast)'],
};
export function Chip({
  children,
  color = 'default',
  icon,
  size = 'm',
}: {
  children?: ReactNode;
  color?: string;
  icon?: IconName;
  size?: 's' | 'm';
}) {
  const [bg, fg] = CHIP_MAP[color] || CHIP_MAP.default;
  return (
    <span
      style={{
        background: bg,
        color: fg,
        borderRadius: 'var(--radius-sm)',
        fontWeight: 700,
        fontSize: size === 's' ? 'var(--fs-caption)' : 'var(--fs-body-sm)',
        lineHeight: size === 's' ? '18px' : '20px',
        height: size === 's' ? 'var(--chip-h-sm)' : 'var(--chip-h-md)',
        padding: size === 's' ? '0 6px' : '0 var(--space-2)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-1)',
        whiteSpace: 'nowrap',
      }}
    >
      {icon && <Icon name={icon} size={size === 's' ? 12 : 13} />}
      {children}
    </span>
  );
}

// status → chip color map (single source of truth for semantics)
export const STATUS_TONE: Record<string, string> = {
  'In Stock': 'success',
  Active: 'success',
  Approved: 'success',
  Validated: 'success',
  Dispensed: 'success',
  Closed: 'success',
  'Low Stock': 'warning',
  'Expiring Soon': 'warning',
  Pending: 'warning',
  'Pending Approval': 'warning',
  Hold: 'warning',
  Quarantine: 'warning',
  'In Progress': 'warning',
  Backorder: 'warning',
  Expired: 'error',
  'Out of Stock': 'error',
  Rejected: 'error',
  Recalled: 'error',
  Controlled: 'solidError',
  Archived: 'default',
  Draft: 'default',
  Info: 'info',
  // STOCKS state models (inward, dispense, trays, transfers, backorders, alerts)
  Posted: 'success',
  'Ready to Post': 'info',
  Voided: 'default',
  Corrected: 'info',
  Picking: 'info',
  'Partially Picked': 'warning',
  Complete: 'success',
  Incomplete: 'warning',
  // Dispensing worklist stages
  'Ready to Pick': 'warning',
  'Accuracy Check': 'info',
  'Waiting Collection': 'success',
  Collected: 'success',
  'In Transit': 'info',
  Discrepancy: 'error',
  'Booked In': 'success',
  Received: 'success',
  Dispatched: 'info',
  Open: 'info',
  Used: 'warning',
  Consumed: 'default',
  Applied: 'success',
  'Partially Fulfilled': 'warning',
  Fulfilled: 'success',
  Acknowledged: 'info',
  Resolved: 'success',
  'Auto-resolved': 'success',
  'Expired – Approval Needed': 'error',
  Override: 'warning',
  Scanned: 'success',
  OK: 'success',
  Mismatch: 'error',
  Allocated: 'info',
  Released: 'default',
  Reservation: 'info',
  'Dispensed (partial)': 'warning',
  Assembling: 'warning',
  Verification: 'info',
  Cancelled: 'default',
  Owing: 'warning',
  'Awaiting Supplier': 'warning',
  'Awaiting Patient Collection': 'info',
  'Stock Received': 'info',
  'Ready For Collection': 'success',
  'Ready To Pick': 'warning',
  Inbound: 'info',
  Outbound: 'violet',
  Quarantined: 'warning',
  Restocked: 'success',
  Disposal: 'default',
  'RMA Drafted': 'info',
  Disposed: 'default',
  Returned: 'success',
  GSL: 'success',
  P: 'warning',
  POM: 'error',
  // PRESCRIPTIONS state model (queue, lines, scan traffic light, Spine)
  New: 'info',
  Parsed: 'info',
  'Awaiting Allocation': 'warning',
  'Awaiting Delivery': 'warning',
  MCS: 'error',
  'Awaiting Collection': 'info',
  'Product Not Available': 'error',
  'Not Available': 'error',
  'Tariff Exceeded': 'warning',
  'Partially Dispensed': 'warning',
  'Returned to Spine': 'error',
  Match: 'success',
  'Not Scanned': 'default',
  // MULTI-STORE request + discrepancy lifecycle
  'Awaiting Approval': 'warning',
  Amended: 'warning',
  'Under Review': 'warning',
  Urgent: 'error',
  Normal: 'default',
};
export function StatusChip({ s, icon }: { s: string; icon?: IconName }) {
  return (
    <Chip color={STATUS_TONE[s] || 'default'} icon={icon}>
      {s}
    </Chip>
  );
}

export type Tone = 'violet' | 'success' | 'warning' | 'error' | 'info';

/** Initials from a name: "John Smith" → "JS", "hp.admin" → "HA", single token → first letter. */
function initialsOf(name?: string): string {
  const parts = (name ?? '')
    .trim()
    .split(/[\s._-]+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function Avatar({
  size = 36,
  src,
  name,
  ring,
}: {
  size?: number;
  /** a real uploaded photo URL; when absent we render initials from `name` */
  src?: string;
  /** used for the initials fallback (and a deterministic colour) when there's no photo */
  name?: string;
  ring?: boolean;
}) {
  const ringShadow = ring ? '0 0 0 2px var(--bg-paper), 0 0 0 4px var(--primary-main)' : 'none';
  if (!src) {
    const initials = initialsOf(name);
    // deterministic hue from the name so each person keeps a stable colour
    const hue = [...(name ?? '?')].reduce((acc, c) => acc + c.charCodeAt(0), 0) % 360;
    return (
      <div
        aria-label={name}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `hsl(${hue} 52% 88%)`,
          color: `hsl(${hue} 55% 26%)`,
          fontWeight: 700,
          fontSize: Math.round(size * 0.4),
          lineHeight: 1,
          userSelect: 'none',
          boxShadow: ringShadow,
        }}
      >
        {initials}
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={name ?? ''}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        objectFit: 'cover',
        boxShadow: ringShadow,
      }}
    />
  );
}

export function Card({
  title,
  subtitle,
  action,
  children,
  style,
  pad,
  flat,
  testId,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  children?: ReactNode;
  style?: CSSProperties;
  pad?: boolean;
  /** Hairline border instead of a shadow — for in-flow surfaces (worklists,
   *  settings); the shadow stays for true overlays and dashboard cards. */
  flat?: boolean;
  /** Stable data-testid for the QA suites (Part B). */
  testId?: string;
}) {
  return (
    <div
      data-testid={testId}
      style={{
        background: 'var(--bg-card)',
        borderRadius: 'var(--radius-lg)',
        ...(flat ? { border: '1px solid var(--divider)' } : { boxShadow: 'var(--shadow-card)' }),
        ...style,
      }}
    >
      {(title || action) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px 0',
          }}
        >
          <div>
            <h3 className="h6" style={{ margin: 0, whiteSpace: 'nowrap' }}>
              {title}
            </h3>
            {subtitle && (
              <p className="caption" style={{ margin: '2px 0 0' }}>
                {subtitle}
              </p>
            )}
          </div>
          {action}
        </div>
      )}
      {pad ? <div style={{ padding: 'var(--space-5)' }}>{children}</div> : children}
    </div>
  );
}

/* ---------------- form controls ---------------- */
export const inputStyle: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  height: 'var(--control-h-md)', // was a drifted 44 next to 38px buttons (WS-A control sizing)
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--grey-300)',
  padding: '0 var(--space-4)',
  fontSize: 'var(--fs-emphasis)',
  color: 'var(--text-primary)',
  outline: 'none',
  background: 'var(--bg-card)',
  transition: 'border-color var(--duration-fast) var(--ease)',
};
export function Field({
  label,
  children,
  hint,
  required,
}: {
  label?: ReactNode;
  children?: ReactNode;
  hint?: ReactNode;
  required?: boolean;
}) {
  return (
    <label style={{ display: 'block', minWidth: 0 }}>
      {label && (
        <span
          style={{
            display: 'block',
            fontSize: 'var(--fs-body-sm)',
            fontWeight: 600,
            color: 'var(--text-secondary)',
            marginBottom: 6,
          }}
        >
          {label}
          {required && <span style={{ color: 'var(--error-dark)' }}> *</span>}
        </span>
      )}
      {children}
      {hint && (
        <span className="caption" style={{ display: 'block', marginTop: 5 }}>
          {hint}
        </span>
      )}
    </label>
  );
}

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  const [f, setF] = useState(false);
  return (
    <input
      {...props}
      onFocus={(e) => {
        setF(true);
        props.onFocus?.(e);
      }}
      onBlur={(e) => {
        setF(false);
        props.onBlur?.(e);
      }}
      style={{
        ...inputStyle,
        borderColor: f ? 'var(--primary-main)' : 'var(--grey-300)',
        boxShadow: f ? '0 0 0 3px var(--primary-soft)' : 'none',
        ...props.style,
      }}
    />
  );
}

export type SelectOption = string | { value: string; label: string };
export function Select({
  value,
  onChange,
  options,
  placeholder,
  style,
  disabled,
  ariaLabel,
}: {
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: SelectOption[];
  placeholder?: string;
  style?: CSSProperties;
  disabled?: boolean;
  /** Accessible name when there's no visible <label> (WCAG select-name, BUG-A11Y-001). */
  ariaLabel?: string;
}) {
  return (
    <div style={{ position: 'relative' }}>
      <select
        value={value}
        onChange={onChange}
        disabled={disabled}
        aria-label={ariaLabel ?? placeholder}
        style={{
          ...inputStyle,
          appearance: 'none',
          paddingRight: 38,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          ...style,
        }}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((o) =>
          typeof o === 'string' ? (
            <option key={o} value={o}>
              {o}
            </option>
          ) : (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ),
        )}
      </select>
      <span
        style={{
          position: 'absolute',
          right: 12,
          top: '50%',
          transform: 'translateY(-50%)',
          pointerEvents: 'none',
          color: 'var(--text-secondary)',
        }}
      >
        <Icon name="chevdown" size={18} />
      </span>
    </div>
  );
}

/** Searchable combobox — filters options as user types. */
export function Combobox({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  displayValue,
  onSearchChange,
  loading,
}: {
  value?: string;
  onChange?: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  /** Override display text when closed (useful when options are dynamic/paged). */
  displayValue?: string;
  /** Called when the search text changes (for server-side search). */
  onSearchChange?: (search: string) => void;
  /** Show loading indicator instead of "No matches". */
  loading?: boolean;
}) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [justSelected, setJustSelected] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  // Set while an option is being clicked so the spurious re-focus that follows
  // (the input keeps focus via the option's preventDefault) does not reopen the
  // dropdown and wipe the just-made selection — that was making the picked value
  // appear blank until the user clicked elsewhere.
  const selectingRef = useRef(false);

  // Sync justSelected with displayValue when displayValue changes (parent-driven)
  useEffect(() => {
    if (displayValue && !open) {
      setJustSelected(displayValue);
    }
  }, [displayValue, open]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const normalized = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
  const selected = normalized.find((o) => o.value === value);
  const filtered = onSearchChange
    ? normalized // server-side filtering — show all results
    : search
      ? normalized.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
      : normalized;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        type="text"
        value={open ? search : justSelected || displayValue || selected?.label || ''}
        onChange={(e) => {
          setSearch(e.target.value);
          setJustSelected('');
          onSearchChange?.(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={() => {
          // Ignore the re-focus that immediately follows an option click.
          if (selectingRef.current) {
            selectingRef.current = false;
            return;
          }
          setOpen(true);
          setSearch('');
          setJustSelected('');
          onSearchChange?.('');
        }}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        style={{
          ...inputStyle,
          cursor: disabled ? 'not-allowed' : 'text',
          opacity: disabled ? 0.6 : 1,
          paddingRight: value ? 30 : undefined,
        }}
      />
      {value && !open && !disabled && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onChange?.('');
            setJustSelected('');
            setSearch('');
          }}
          style={{
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            color: 'var(--text-secondary)',
            fontSize: 'var(--fs-emphasis)',
            lineHeight: 1,
            padding: '2px 4px',
            borderRadius: 'var(--radius-xs)',
          }}
          title="Clear selection"
        >
          ×
        </button>
      )}
      {open && filtered.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 999,
            maxHeight: 220,
            overflowY: 'auto',
            background: 'var(--bg-card)',
            border: '1px solid var(--grey-300)',
            borderRadius: 'var(--radius-md)',
            marginTop: 'var(--space-1)',
            boxShadow: 'var(--shadow-z8)',
          }}
        >
          {filtered.map((o) => (
            <div
              key={o.value}
              data-testid="combobox-option"
              onMouseDown={(e) => {
                // Prevent input blur race; handle selection directly. Flag the
                // selection so the trailing re-focus is ignored (see onFocus).
                e.preventDefault();
                selectingRef.current = true;
              }}
              onClick={() => {
                onChange?.(o.value);
                setJustSelected(o.label);
                setSearch('');
                setOpen(false);
                // Blur input to prevent onFocus from re-opening
                const input = ref.current?.querySelector('input');
                input?.blur();
                // Clear the guard after any synchronous re-focus has been ignored,
                // so the next genuine click on the field still opens the dropdown.
                setTimeout(() => {
                  selectingRef.current = false;
                }, 0);
              }}
              style={{
                padding: '8px 14px',
                fontSize: 'var(--fs-body)',
                cursor: 'pointer',
                background: o.value === value ? 'var(--primary-lighter)' : 'transparent',
                fontWeight: o.value === value ? 600 : 400,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background = 'var(--grey-100)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background =
                  o.value === value ? 'var(--primary-lighter)' : 'transparent';
              }}
            >
              {o.label}
            </div>
          ))}
        </div>
      )}
      {open && filtered.length === 0 && search && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 999,
            padding: '10px 14px',
            background: 'var(--bg-card)',
            border: '1px solid var(--grey-300)',
            borderRadius: 'var(--radius-md)',
            marginTop: 'var(--space-1)',
            fontSize: 'var(--fs-body)',
            color: 'var(--text-secondary)',
          }}
        >
          {loading ? 'Searching…' : 'No matches'}
        </div>
      )}
    </div>
  );
}

export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      style={{
        ...inputStyle,
        height: 'auto',
        minHeight: 84,
        padding: '12px 14px',
        resize: 'vertical',
        lineHeight: 1.5,
        ...props.style,
      }}
    />
  );
}

export function Toggle({
  on,
  onClick,
  readOnly,
  title,
}: {
  on?: boolean;
  onClick?: () => void;
  /** Render as a non-interactive STATE indicator. Use when a value is shown but cannot (yet) be
   *  changed — a toggle that silently discards the click is worse than one that can't be clicked. */
  readOnly?: boolean;
  title?: string;
}) {
  return (
    <button
      onClick={readOnly ? undefined : onClick}
      disabled={readOnly}
      title={title}
      style={{
        width: 40,
        height: 22,
        borderRadius: 'var(--radius-pill)',
        border: 'none',
        cursor: readOnly ? 'default' : 'pointer',
        opacity: readOnly ? 0.55 : 1,
        position: 'relative',
        background: on ? 'var(--primary-main)' : 'var(--grey-48)',
        transition: 'background var(--duration-fast) var(--ease)',
        flex: 'none',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 2,
          left: on ? 20 : 2,
          width: 18,
          height: 18,
          borderRadius: '50%',
          background: 'var(--bg-card)',
          transition: 'left var(--duration-fast) var(--ease)',
          boxShadow: 'var(--shadow-z1)',
        }}
      />
    </button>
  );
}

export function ProgressBar({
  pct,
  color = 'var(--primary-main)',
  height = 8,
}: {
  pct: number;
  color?: string;
  height?: number;
}) {
  const over = pct > 100;
  return (
    <div
      style={{
        height,
        borderRadius: 'var(--radius-pill)',
        background: 'var(--grey-200)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: Math.min(100, pct) + '%',
          height: '100%',
          borderRadius: 'var(--radius-pill)',
          background: over ? 'var(--error-main)' : color,
          transition: 'width var(--duration-base) var(--ease)',
        }}
      />
    </div>
  );
}

/* ---------------- overlays ---------------- */
export function Modal({
  open,
  onClose,
  children,
  width = 480,
  title,
  subtitle,
  testId,
}: {
  open?: boolean;
  onClose?: () => void;
  children?: ReactNode;
  width?: number;
  title?: ReactNode;
  subtitle?: ReactNode;
  /** Stable data-testid on the dialog for the QA suites (Part B). */
  testId?: string;
}) {
  // Escape closes the dialog — expected of every modal, and the only way out for keyboard users
  // (the backdrop click below is mouse-only). Bound while open, on the document, so it works no
  // matter where focus sits. Declared before the early return to keep hook order stable.
  useEffect(() => {
    if (!open || !onClose) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--overlay-backdrop)',
        zIndex: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'fade .15s ease',
        padding: 'var(--space-5)',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        // dialogs need an accessible name (axe aria-dialog-name) — the title
        // when it's plain text, an honest generic otherwise
        aria-label={typeof title === 'string' && title ? title : 'Dialog'}
        data-testid={testId}
        onClick={(e) => e.stopPropagation()}
        style={{
          width,
          maxWidth: '100%',
          maxHeight: '90vh',
          overflowX: 'hidden',
          overflowY: 'auto',
          whiteSpace: 'normal',
          background: 'var(--bg-card)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-dropdown)',
          // 28 snapped down to the 24 step (WS-A spacing adoption; denser is safer)
          padding: 'var(--space-5)',
          animation: 'pop .18s ease',
        }}
      >
        {title && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--space-3)',
              marginBottom: 'var(--space-5)',
            }}
          >
            <div style={{ flex: 1 }}>
              <h2 className="h5" style={{ margin: 0 }}>
                {title}
              </h2>
              {subtitle && (
                <p className="body2" style={{ margin: '4px 0 0', color: 'var(--text-secondary)' }}>
                  {subtitle}
                </p>
              )}
            </div>
            <button aria-label="Close" onClick={onClose} style={{ ...iconBtn, marginLeft: 'auto' }}>
              <Icon name="close" size={20} color="var(--text-secondary)" />
            </button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

// Per-mount serial for drawer history entries: the deferred consume below must
// only ever pop the entry THIS drawer pushed — a truthy flag alone races with the
// next drawer (navigate out of drawer A, target page deep-link-opens drawer B,
// A's deferred consume would pop B's entry and close it).
let drawerSerial = 0;

export function Drawer({
  open,
  onClose,
  children,
  width = 720,
  title,
  subtitle,
  action,
}: {
  open?: boolean;
  onClose?: () => void;
  children?: ReactNode;
  width?: number;
  /** Header contract (same shape as Modal): a title renders the standard
   *  header bar — h5 title, caption subtitle, close button. `action` slots
   *  chips/status/buttons just before the close. */
  title?: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
}) {
  // The drawer surface contract (docs/design-patterns.md §1, audit #8): opening
  // pushes a lightweight history entry so browser Back closes the drawer instead
  // of leaving the list page, and Escape closes like every other overlay. All of
  // it lives here so consumers get it by rendering <Drawer> — no per-page wiring.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  // Whether OUR history entry is currently on top of the stack. Refs (not state):
  // they survive StrictMode's dev-mode effect replay without re-pushing.
  const idRef = useRef(0);
  const pushedRef = useRef(false);
  const consumeTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (!open) return;
    // Effect replay (StrictMode) re-runs before the deferred consume below fires —
    // cancel it so the replay reuses the entry instead of popping it.
    clearTimeout(consumeTimer.current);
    if (!pushedRef.current) {
      idRef.current = ++drawerSerial;
      window.history.pushState({ piqDrawer: idRef.current }, '');
      pushedRef.current = true;
    }
    const onPop = () => {
      // Browser Back consumed our entry: close the drawer, stay on the page.
      pushedRef.current = false;
      closeRef.current?.();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // A modal stacked above the drawer (confirm dialogs) owns Escape.
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      closeRef.current?.();
    };
    window.addEventListener('popstate', onPop);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('popstate', onPop);
      document.removeEventListener('keydown', onKey);
      // Programmatic close (Esc, backdrop, close button, unmount): consume the
      // entry we pushed so the stack doesn't grow — deferred a tick so a
      // StrictMode replay can cancel it, and only when the top entry is still
      // OURS (a real navigation from inside the drawer buries it; a follow-up
      // drawer may already have pushed its own).
      consumeTimer.current = setTimeout(() => {
        const top = (window.history.state as { piqDrawer?: number } | null)?.piqDrawer;
        if (pushedRef.current && top === idRef.current) {
          pushedRef.current = false;
          window.history.back();
        }
      }, 0);
    };
  }, [open]);

  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--overlay-backdrop)',
        zIndex: 55,
        display: 'flex',
        justifyContent: 'flex-end',
        animation: 'fade .15s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width,
          maxWidth: '94vw',
          height: '100%',
          background: 'var(--bg-panel)',
          boxShadow: 'var(--shadow-dropdown)',
          overflowY: 'auto',
          animation: 'slideL .22s ease',
        }}
      >
        {title != null && (
          <div
            style={{
              padding: '24px 28px',
              borderBottom: '1px solid var(--divider)',
              display: 'flex',
              alignItems: 'center',
              gap: 14,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <h2 className="h5" style={{ margin: 0 }}>
                {title}
              </h2>
              {subtitle && (
                <p className="caption" style={{ margin: '4px 0 0' }}>
                  {subtitle}
                </p>
              )}
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
              {action}
              <button aria-label="Close" onClick={onClose} style={iconBtn}>
                <Icon name="close" size={20} color="var(--text-secondary)" />
              </button>
            </div>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
export const iconBtn: CSSProperties = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  padding: 6,
  borderRadius: 'var(--radius-md)',
  display: 'inline-flex',
  alignItems: 'center',
};

export type TabItem = string | { id: string; label: ReactNode; badge?: ReactNode };
export function Tabs({
  tabs,
  active,
  onChange,
}: {
  tabs: TabItem[];
  active: string;
  onChange: (id: string) => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 2,
        borderBottom: '1px solid var(--divider)',
        overflowX: 'auto',
      }}
    >
      {tabs.map((t) => {
        const id = typeof t === 'string' ? t : t.id;
        const label = typeof t === 'string' ? t : t.label;
        const badge = typeof t === 'string' ? undefined : t.badge;
        return (
          <button
            key={id}
            onClick={() => onChange(id)}
            style={{
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: 'var(--fs-emphasis)',
              fontWeight: 600,
              padding: '14px 14px',
              whiteSpace: 'nowrap',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              // -ink token, not primary-main: on dark elevated panels/cards the
              // main violet is < 4.5:1, so dark mode lifts the active-tab ink.
              color: active === id ? 'var(--primary-ink)' : 'var(--text-secondary)',
              boxShadow: active === id ? 'inset 0 -2px 0 var(--primary-main)' : 'none',
            }}
          >
            {label}
            {badge != null && (
              <span
                style={{
                  background: active === id ? 'var(--primary-lighter)' : 'var(--grey-16)',
                  // grey-700 on grey-16 for AA at 11px bold, matching Chip default (BUG-A11Y-001)
                  color: active === id ? 'var(--primary-dark)' : 'var(--grey-700)',
                  borderRadius: 'var(--radius-pill)',
                  fontSize: 'var(--fs-caption)',
                  fontWeight: 700,
                  padding: '1px 7px',
                }}
              >
                {badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function EmptyState({
  icon = 'box',
  title,
  body,
  action,
  flow,
}: {
  icon?: IconName;
  title?: ReactNode;
  body?: ReactNode;
  action?: ReactNode;
  /** Editorial variant (Wave D): one sentence in the page flow instead of the
   *  centered icon monument — for worklists where "empty" is unremarkable. */
  flow?: boolean;
}) {
  if (flow) {
    return (
      <p
        className="body2"
        style={{ margin: 0, padding: '14px 4px', color: 'var(--text-secondary)' }}
      >
        {title}
        {body != null && <> — {body}</>}
        {action != null && <span style={{ marginLeft: 10 }}>{action}</span>}
      </p>
    );
  }
  return (
    <div
      style={{
        padding: '56px 32px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 'var(--space-3)',
      }}
    >
      <div
        style={{
          width: 60,
          height: 60,
          borderRadius: '50%',
          background: 'var(--grey-200)',
          color: 'var(--text-disabled)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon name={icon} size={28} />
      </div>
      <h3 className="h6" style={{ margin: 0 }}>
        {title}
      </h3>
      {body && (
        <p className="body2" style={{ margin: 0, maxWidth: 420, color: 'var(--text-secondary)' }}>
          {body}
        </p>
      )}
      {action}
    </div>
  );
}

// read-only banner shown to Compliance Officer
export function ReadOnlyBanner() {
  const { readOnly, roleName } = useStore();
  if (!readOnly) return null;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 16px',
        borderRadius: 'var(--radius-md)',
        background: 'var(--info-soft)',
        color: 'var(--info-dark)',
        marginBottom: 20,
        fontSize: 'var(--fs-body)',
        fontWeight: 600,
      }}
    >
      <Icon name="eye" size={18} color="var(--info-main)" />
      Read-only mode — {roleName} has full audit-trail visibility but cannot modify records.
    </div>
  );
}

/* ---------------- toasts ---------------- */
export function Toasts() {
  const { state, dispatch } = useStore();
  const tones: Record<string, [string, IconName]> = {
    success: ['var(--success-dark)', 'checkcircle'],
    error: ['var(--error-dark)', 'xcircle'],
    warning: ['var(--warning-dark)', 'alerts'],
    info: ['var(--info-main)', 'info'],
  };
  return (
    <div
      style={{
        position: 'fixed',
        right: 24,
        bottom: 24,
        zIndex: 90,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        alignItems: 'flex-end',
      }}
    >
      {state.toasts.map((t) => {
        const [col, ic] = tones[t.tone] || tones.success;
        return (
          <div
            key={t.id}
            onClick={() => dispatch({ type: 'UNTOAST', id: t.id })}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              background: 'var(--bg-card)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-dropdown)',
              padding: '14px 18px',
              minWidth: 280,
              maxWidth: 420,
              cursor: 'pointer',
              animation: 'slideUp .22s ease',
              borderLeft: `4px solid ${col}`,
            }}
          >
            <Icon name={ic} size={22} color={col} />
            <span
              style={{
                fontSize: 'var(--fs-emphasis)',
                fontWeight: 600,
                color: 'var(--text-primary)',
              }}
            >
              {t.msg}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export const td: CSSProperties = {
  padding: '14px 16px',
  fontSize: 'var(--fs-emphasis)',
  color: 'var(--text-secondary)',
  whiteSpace: 'nowrap',
};
export const thStyle: CSSProperties = {
  textAlign: 'left',
  fontSize: 'var(--fs-body-sm)',
  fontWeight: 700,
  color: 'var(--text-secondary)',
  padding: 'var(--space-3) var(--space-4)',
  whiteSpace: 'nowrap',
  textTransform: 'none',
};
export const mono: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 'var(--fs-body-sm)',
  color: 'var(--text-primary)',
  letterSpacing: '-.2px',
};
